// filepath: apps/web/src/features/proc/useFailedInstances.ts
//
// FR-UI-PROC-1 AC-2 / AC-3 — TanStack Query hook for the failed-instance
// dashboard list.
//
// Issues GET /api/processes/instances?state=failed and returns the
// failed-instance list envelope. The cache key includes tenantSlug
// as the 2nd segment (INV-1) so tenant-A and tenant-B cache entries
// are isolated.
//
// Wire shape: FailedInstanceListResponse (internal/obs/handler.go:62-100).
// The endpoint is tenant-scoped server-side via the kernel resolver
// middleware; the SPA trusts the wire contract and renders exactly
// what the server returns (INV-2 / FR-AUTH-7).
//
// Failure surface:
//   - 5xx / network → ApiError(status=0|5xx, code=network_error|http_5xx)
//     → caller maps to FR-UI-CORE-10 `fetch-failure` state.
//   - 403 → ApiError(403, forbidden) → caller maps to
//     `permission-denied`.
//   - 429 with Retry-After → ApiError(429, rate_limited, retryAfter)
//     → caller maps to `rate-limit`.
//   - 400 (state != failed) → ApiError(400, unsupported_state) → caller
//     maps to `fetch-failure` (server contract violation; defensive).
//   - 200 + empty instances[] → SUCCESSFUL empty result. Rendered
//     as empty-state chrome (NOT a permission-denied banner — would
//     leak existence).

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import { procKeys, type FailedInstanceListResponse } from "./types";

export interface UseFailedInstancesResult {
  data: FailedInstanceListResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: ApiError | null;
  refetch: () => Promise<unknown>;
}

/**
 * useFailedInstances — TanStack Query hook backing the
 * failed-instance dashboard list.
 *
 * See file header for invariants and wire-contract notes.
 */
export function useFailedInstances(apiBase = "/api"): UseFailedInstancesResult {
  const { tenantSlug } = useTenant();

  const query: UseQueryResult<FailedInstanceListResponse, ApiError> = useQuery<
    FailedInstanceListResponse,
    ApiError
  >({
    queryKey: procKeys.failedInstances(tenantSlug),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set("state", "failed");
      const url = `${apiBase}/processes/instances?${params.toString()}`;

      let response: Response;
      try {
        response = await apiFetch(url, {
          init: {
            headers: { Accept: "application/json" },
            signal,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const wire = (await response.json()) as FailedInstanceListResponse;
      return normalise(wire);
    },
    // 30s freshness — same as the list-view data class.
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Defensive normaliser. The server returns a flat array; this is
 * mostly a no-op, but it guarantees the consumer receives an array
 * (never `null` / `undefined`) so the renderer's `.length` access
 * is always safe. */
function normalise(
  wire: FailedInstanceListResponse,
): FailedInstanceListResponse {
  return {
    instances: Array.isArray(wire.instances) ? wire.instances : [],
  };
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function parseRetryAfter(header: string | null): number | null {
  if (header === null) return null;
  const trimmed = header.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (Number.isFinite(n) && n >= 0) return n;
  // Retry-After may be an HTTP-date; treat non-numeric as unknown.
  return null;
}
